import {useEffect, useMemo, useRef} from 'react';
import {graphql} from 'react-relay';
import {useForecastFetchQuery} from '../../../../shared/hooks/useForecastFetchQuery';
import {commitAsync, mutationClass} from '../clientAssetMutations';
import {PICKER_FAILED, PROVIDER_KEY, type CloudProvider, type CloudProviderConfig} from './cloudConnect';
import type {cloudProviderQueriesProvidersQuery} from './__generated__/cloudProviderQueriesProvidersQuery.graphql';

// The providers the workspace has configured, with the public values the connect popup
// and the picker need, and the viewer's own connection. Fetched on demand by the cloud button, so
// useForecastFetchQuery is the sanctioned hook (as for the default approver in CreateClientAssetModal).
const providersQuery = graphql`
	query cloudProviderQueriesProvidersQuery {
		viewer {
			id
			cloudStorageProviders {
				provider
				clientId
				pickerApiKey
				appId
				redirectUri
				connection {
					accountEmail
					status
				}
			}
		}
	}
`;

const connectMutation = graphql`
	mutation cloudProviderQueriesConnectMutation($input: ConnectCloudStorageInput!) {
		connectCloudStorage(input: $input) {
			provider
			accountEmail
			status
		}
	}
`;

const pickerTokenMutation = graphql`
	mutation cloudProviderQueriesPickerTokenMutation($input: IssueCloudPickerTokenInput!) {
		issueCloudPickerToken(input: $input) {
			accessToken
			expiresAt
		}
	}
`;

export const ConnectMutation = mutationClass(connectMutation);
export const PickerTokenMutation = mutationClass(pickerTokenMutation);

export type PickerToken = {accessToken: string; expiresAt: string};

/**
 * A short-lived access token for the provider's picker, minted server-side from the stored grant.
 * `resource` is OneDrive's SharePoint origin for its picker. A coded refusal (not connected,
 * revoked, …) rejects with its translation id, as commitAsync does.
 */
export const issuePickerToken = async (input: {provider: CloudProvider; resource?: string}): Promise<PickerToken> => {
	const response = await commitAsync<{issueCloudPickerToken: PickerToken | null}>(PickerTokenMutation, input);
	const token = response.issueCloudPickerToken;
	if (!token) throw new Error(PICKER_FAILED);
	return token;
};

/** Trades the popup's one-time code for a stored connection. */
export const connectCloudStorage = (input: {provider: CloudProvider; code: string}) =>
	commitAsync<{connectCloudStorage: {provider: CloudProvider; accountEmail: string; status: string} | null}>(
		ConnectMutation,
		input
	);

type ProvidersResponse = cloudProviderQueriesProvidersQuery['response'];

/**
 * The configured providers, fetched once while `enabled` (the feature flag). A provider this
 * client does not know yet is dropped rather than offered with no flow behind it.
 */
export const useCloudProviders = (
	enabled: boolean
): {providers: CloudProviderConfig[]; loading: boolean; settled: boolean; refresh: () => void} => {
	const {data, fetch, loading} = useForecastFetchQuery<ProvidersResponse, Record<string, never>>(providersQuery);
	const fetched = useRef(false);

	useEffect(() => {
		// No onError: a failed lookup only means no cloud button.
		if (enabled) {
			fetched.current = true;
			fetch();
		}
	}, [enabled]);

	const rows = enabled ? data?.viewer?.cloudStorageProviders : null;
	// The first lookup has answered or failed. A refresh keeps the answer it already has.
	const settled = !!rows || (fetched.current && !loading);
	const providers = useMemo(
		() =>
			(rows ?? [])
				.filter(row => Object.prototype.hasOwnProperty.call(PROVIDER_KEY, row.provider))
				.map(row => ({
					provider: row.provider as CloudProvider,
					clientId: row.clientId,
					pickerApiKey: row.pickerApiKey ?? null,
					appId: row.appId ?? null,
					redirectUri: row.redirectUri,
					connection: row.connection
						? {accountEmail: row.connection.accountEmail, status: row.connection.status}
						: null,
				})),
		[rows]
	);

	return {providers, loading, settled, refresh: () => fetch()};
};
